/**
 * [INPUT]: 依赖 AuthProvider、zinnercms Button/Input/Label、public/zinner-logo.png
 * [OUTPUT]: 对外提供 Login 邮箱密码登录页
 * [POS]: pages 认证入口
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Mail } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/providers/AuthContext'
import { Button } from '@/zinnercms-design-system/ui/button'
import { Input } from '@/zinnercms-design-system/ui/input'
import { Label } from '@/zinnercms-design-system/ui/label'
import { InlineLoading } from '@/components/LoadingDots'

export default function Login() {
  const { signIn, loading, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isAuthenticated) navigate('/admin/dashboard', { replace: true })
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!formData.email.trim()) return setError('请输入邮箱')
    if (!formData.password.trim()) return setError('请输入密码')
    setSubmitting(true)
    try {
      await signIn(formData.email, formData.password)
      navigate('/admin/dashboard', { replace: true })
    } catch (err) {
      if (err?.code === 'NOT_ADMIN') {
        toast.error('你不是管理员')
        setError('你不是管理员，无权登录后台')
      } else {
        setError(err.message || '登录失败，请检查邮箱和密码')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const busy = submitting || loading

  return (
    <div className="flex min-h-dvh items-center justify-center bg-sidebar px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/zinner-logo.png" alt="Zinner" className="mb-4 size-16 rounded-full object-cover" />
          <h1 className="text-xl font-semibold tracking-tight">
            <span className="text-primary">Zinner</span> CMS
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">高净值关系运营后台</p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="email">邮箱</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                className="pl-9"
                value={formData.email}
                onChange={(e) => setFormData((s) => ({ ...s, email: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">密码</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="pl-9"
                value={formData.password}
                onChange={(e) => setFormData((s) => ({ ...s, password: e.target.value }))}
              />
            </div>
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? <InlineLoading /> : '登录'}
          </Button>
        </form>
      </div>
    </div>
  )
}
